/**
 * Telling a person's name from a company's.
 *
 * The invite pipeline has only ever carried one name — the business name — and
 * the page used to slice a "first name" off it. For a sole trader that reads
 * fine; for "Noble Roots Landscaping" it greets the owner as "Noble". A wrong
 * first name is worse than none: it is the exact tell of a mail-merge, on the
 * one page whose whole argument is that a person made this by hand.
 *
 * So the contact name is now its own field (`website.contactName`, filled from
 * the lead's Owner Name), and this module is the gate in front of it. It is
 * deliberately strict — the copy has a good unnamed variant, so a name we are
 * not sure about should be dropped rather than guessed at.
 */

/** Words that appear in company names and never in a person's. */
const COMPANY_WORDS = new Set([
  'llc', 'inc', 'incorporated', 'corp', 'corporation', 'co', 'company', 'ltd',
  'group', 'holdings', 'enterprises', 'industries', 'partners', 'associates',
  'services', 'service', 'solutions', 'systems', 'contracting', 'contractors',
  'construction', 'builders', 'remodeling', 'restoration', 'maintenance',
  'plumbing', 'heating', 'cooling', 'hvac', 'air', 'electric', 'electrical',
  'roofing', 'landscaping', 'lawn', 'cleaning', 'pest', 'paving', 'flooring',
  'painting', 'concrete', 'sons', 'brothers', 'bros', 'family', 'the',
]);

/** Someone typed a placeholder into the Owner Name field rather than a name. */
const NON_NAMES = new Set([
  'owner', 'manager', 'admin', 'office', 'info', 'contact', 'team', 'staff',
  'customer', 'client', 'unknown', 'n/a', 'na', 'none', 'test',
]);

const tokenize = (value: string): string[] =>
  value
    .toLowerCase()
    .replace(/[^a-z\s'-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

/** "ANDY" → "Andy", "andy" → "Andy", "McRae" → "McRae" (already mixed case). */
function tidyCase(word: string): string {
  if (word === word.toLowerCase() || word === word.toUpperCase()) {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }
  return word;
}

/**
 * The first name to greet someone by, or `''` when we do not have one.
 *
 * `businessName` is passed so a company name sitting in the contact field is
 * caught even when it carries no obvious company word — "Noble Roots" against
 * "Noble Roots Landscaping" is every token accounted for, so it is the company.
 */
export function personalFirstName(
  contactName: string | undefined | null,
  businessName?: string
): string {
  // "Dave Chen, Owner" — the enrichment fields collect a title often enough
  // that dropping the whole entry over it would cost real names.
  const raw = (contactName || '').split(',')[0].trim();
  if (!raw) return '';

  // Digits, ampersands and @ belong to companies and handles, not to names.
  if (/[0-9@&]/.test(raw)) return '';

  const tokens = tokenize(raw);
  // A person introduces themselves in one to four words. More than that is a
  // company name, a name plus a title, or a sentence.
  if (!tokens.length || tokens.length > 4) return '';
  if (tokens.some(token => COMPANY_WORDS.has(token))) return '';
  if (tokens.some(token => NON_NAMES.has(token))) return '';

  // Every word already inside the business name — "Noble", "Noble Roots" —
  // means the business name was copied into the contact field.
  const businessTokens = new Set(tokenize(businessName || ''));
  if (businessTokens.size && tokens.every(token => businessTokens.has(token))) {
    return '';
  }

  const first = raw.trim().split(/\s+/)[0].replace(/^[^A-Za-z]+|[^A-Za-z'-]+$/g, '');
  // Initials ("J.") read as a mail-merge artefact, not as being known.
  if (first.length < 2) return '';

  return tidyCase(first);
}
